import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Trash2, Plus, FileText, CreditCard, MessageCircle, CheckCircle2, Loader2, RefreshCw, Save, Wand2, Download, ExternalLink, Copy, Maximize2, Minimize2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

function computeTotals(lines) {
  // v1.22 rule (user-specified — standard GST extraction):
  //   base_price = MRP − (MRP / (100 + GST%)) × GST% = MRP / (1 + GST%/100)
  //   taxable    = (base_price × qty) × (1 − Disc%/100)
  //   line_total = taxable × (1 + GST%/100)
  // Verified: MRP=100, GST=18%, Disc=30% → base=84.75, taxable=59.32,
  //           GST=10.68, line_total=₹70.00.
  let subtotal = 0, discount = 0, gst = 0, grand = 0, taxable_sum = 0;
  const out = (lines || []).map((l) => {
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const qty      = num(l.quantity);
    const mrp      = num(l.mrp);
    const gstPct   = num(l.gst_pct);
    const discPct  = num(l.discount_pct);
    const gross    = qty * mrp;                    // GST-inclusive MRP × qty (display)
    const basePrice = gstPct > 0 ? mrp / (1 + gstPct / 100) : mrp;   // extract GST
    const baseValue = basePrice * qty;             // ex-GST base
    const disc     = discPct
      ? (baseValue * discPct) / 100
      : num(l.discount_amt);
    const taxable  = Math.max(baseValue - disc, 0);   // base − Disc%
    const g        = (taxable * gstPct) / 100;
    const total    = taxable + g;
    subtotal += gross; discount += disc; gst += g; grand += total; taxable_sum += taxable;
    return { ...l,
             discount_amt: +disc.toFixed(2),
             line_total:   +total.toFixed(2),
             selling_price: qty ? +(taxable / qty).toFixed(2) : +taxable.toFixed(2) };
  });
  return { lines: out, totals: {
    subtotal:       +subtotal.toFixed(2),
    discount_total: +discount.toFixed(2),
    taxable_amount: +taxable_sum.toFixed(2),
    gst_total:      +gst.toFixed(2),
    cgst_total:     +(gst / 2).toFixed(2),
    sgst_total:     +(gst / 2).toFixed(2),
    igst_total: 0,
    grand_total:    +grand.toFixed(2),
  }};
}

const TDS_SECTIONS_UI = [
  { code: "", label: "No TDS" },
  { code: "194Q", label: "194Q — Purchase of goods (0.1%)", rate: 0.1 },
  { code: "194C", label: "194C — Contract (1%)", rate: 1.0 },
  { code: "194J", label: "194J — Professional services (10%)", rate: 10.0 },
  { code: "194H", label: "194H — Commission / brokerage (5%)", rate: 5.0 },
];

function MediaViewer({ url, mime }) {
  if (!url) return <div className="text-sm text-slate-500 border rounded-lg p-6 text-center">No prescription attached.</div>;
  const full = url.startsWith("http") ? url : `${API_BASE.replace(/\/api$/, "")}${url}`;
  const isPdf = (mime || "").includes("pdf") || url.toLowerCase().endsWith(".pdf");
  return (
    <div className="border rounded-lg overflow-hidden bg-slate-50 h-[440px] flex items-center justify-center" data-testid="rx-media-viewer">
      {isPdf ? (
        <iframe title="prescription" src={full} className="w-full h-full" />
      ) : (
        <img src={full} alt="prescription" className="max-w-full max-h-full object-contain" />
      )}
    </div>
  );
}

// v1.52 — stable row key generator. Any time we accept a row from state /
// server, we stamp a random `_key` if missing so the React key never depends
// on the user-editable `name` field (which caused the "single-digit-at-a-time"
// bug: typing "P" changed the key from `line-0` to `P-0`, unmounting the
// input and killing focus after each character).
const _mkKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `k-${Math.random().toString(36).slice(2)}-${Date.now()}`;
export const withStableKeys = (rows) =>
  (rows || []).map((r) => (r._key ? r : { ...r, _key: _mkKey() }));

function LineGrid({ lines, setLines, orderType }) {
  const isLab = orderType === "lab_test" || orderType === "full_body_checkup";
  const setField = (i, f, v) =>
    setLines(lines.map((l, k) => (k === i ? { ...l, [f]: v } : l)));
  const setNum = (i, f, str) =>
    setField(i, f, str === "" ? null : (parseFloat(str) || 0));
  const add = () => setLines([...lines, {
    _key: _mkKey(),
    kind: isLab ? "lab_test" : "medicine",
    name: "", quantity: 1, mrp: null, discount_pct: null, gst_pct: null,
  }]);
  const remove = (i) => setLines(lines.filter((_, k) => k !== i));
  const display = (v) => (v === null || v === undefined || v === 0 ? "" : String(v));
  return (
    <div className="space-y-2" data-testid="rx-lines-grid">
      {lines.length === 0 && <div className="text-xs text-slate-500 px-1">No line items yet. Use "Auto-fill from prescription" or add a row.</div>}
      {lines.map((l, i) => (
        <div key={l._key || l.id || `row-${i}`} className="border rounded-lg p-2 text-xs space-y-1.5" data-testid={`rx-line-${i}`}>
          {/* Row 1 — commercial fields */}
          <div className="flex gap-1.5 items-center flex-wrap">
            <Input className="flex-1 min-w-[160px] h-8 text-xs" placeholder={isLab ? "Test / Package" : "Medicine name"} value={l.name || ""} onChange={(e) => setField(i, "name", e.target.value)} data-testid={`rx-line-name-${i}`} />
            {!isLab && <Input className="w-24 h-8 text-xs" placeholder="Strength" value={l.strength || ""} onChange={(e) => setField(i, "strength", e.target.value)} data-testid={`rx-line-strength-${i}`} />}
            {!isLab && <Input className="w-20 h-8 text-xs" placeholder="Form" value={l.dosage_form || ""} onChange={(e) => setField(i, "dosage_form", e.target.value)} />}
            <Input className="no-spinner w-16 h-8 text-xs text-right" type="number" inputMode="decimal" placeholder="Qty" value={display(l.quantity)} onChange={(e) => setNum(i, "quantity", e.target.value)} data-testid={`rx-line-qty-${i}`} />
            <Input className="no-spinner w-20 h-8 text-xs text-right" type="number" inputMode="decimal" placeholder="MRP" value={display(l.mrp)} onChange={(e) => setNum(i, "mrp", e.target.value)} data-testid={`rx-line-mrp-${i}`} />
            <Input className="no-spinner w-16 h-8 text-xs text-right" type="number" inputMode="decimal" placeholder="Disc%" value={display(l.discount_pct)} onChange={(e) => setNum(i, "discount_pct", e.target.value)} data-testid={`rx-line-disc-${i}`} />
            <Input className="no-spinner w-16 h-8 text-xs text-right" type="number" inputMode="decimal" placeholder="GST%" value={display(l.gst_pct)} onChange={(e) => setNum(i, "gst_pct", e.target.value)} data-testid={`rx-line-gst-${i}`} />
            <div className="w-24 text-right font-medium tabular-nums text-emerald-800" data-testid={`rx-line-total-${i}`}>{inr(l.line_total)}</div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => remove(i)} data-testid={`rx-line-remove-${i}`}><Trash2 size={12} /></Button>
          </div>
          {/* Row 2 — pharma / ERP fields (Pack / Unit / Free removed per v1.12 spec) */}
          <div className="flex gap-1.5 items-center flex-wrap pl-1">
            <Input className="w-24 h-7 text-[11px]" placeholder="HSN/SAC" value={l.hsn_sac || ""} onChange={(e) => setField(i, "hsn_sac", e.target.value)} data-testid={`rx-line-hsn-${i}`} />
            <Input className="w-24 h-7 text-[11px]" placeholder="Batch #" value={l.batch_no || ""} onChange={(e) => setField(i, "batch_no", e.target.value)} data-testid={`rx-line-batch-${i}`} />
            <Input className="w-24 h-7 text-[11px]" placeholder="Expiry MM/YYYY" value={l.expiry_date || ""} onChange={(e) => setField(i, "expiry_date", e.target.value)} data-testid={`rx-line-expiry-${i}`} />
            <Input className="flex-1 min-w-[160px] h-7 text-[11px]" placeholder="Remarks (frequency, duration, notes)" value={l.remarks || ""} onChange={(e) => setField(i, "remarks", e.target.value)} data-testid={`rx-line-remarks-${i}`} />
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="rx-line-add-btn"><Plus size={14} /> Add row</Button>
    </div>
  );
}

function ExtractedList({ meds }) {
  if (!meds || meds.length === 0) {
    return <div className="text-xs text-slate-500 border rounded-lg p-3 text-center">AI extraction pending or unavailable.</div>;
  }
  return (
    <div className="grid grid-cols-1 gap-2 max-h-[220px] overflow-auto pr-1" data-testid="rx-extracted-list">
      {meds.map((m, i) => (
        <div key={m.id || m._key || `extracted-${i}`} className="border rounded-lg p-2 text-xs bg-slate-50" data-testid={`rx-extracted-${i}`}>
          <div className="flex items-center justify-between">
            <div className="font-medium" data-testid={`rx-extracted-${i}-name`}>{m.name}</div>
            <Badge variant="outline" className="text-[10px]">conf {(Number(m.confidence || 0) * 100).toFixed(0)}%</Badge>
          </div>
          <div className="text-[11px] text-slate-500">{m.strength || "—"} · {m.dosage_form || "—"} · qty {m.quantity || "1"}</div>
          {(m.frequency || m.duration) && <div className="text-[11px]">{m.frequency ? `Freq: ${m.frequency}` : ""} {m.duration ? `· Dur: ${m.duration}` : ""}</div>}
          {m.needs_human_verification && <Badge className="bg-amber-100 text-amber-800 text-[10px] mt-1">needs review</Badge>}
        </div>
      ))}
    </div>
  );
}

function StatusBanner({ order, onCopy }) {
  const q = order?.latest_quotation_id;
  const qPdf = order?.latest_quotation_pdf;
  const link = order?.payment_link || {};
  const linkUrl = link.url;
  const linkStatus = link.status;
  const payStatus = order?.payment_status;
  return (
    <div className="grid grid-cols-3 gap-2 border rounded-lg p-2 bg-slate-50 text-xs" data-testid="rx-status-banner">
      <div>
        <div className="text-[10px] uppercase text-slate-500">Quotation</div>
        <div className="font-mono truncate" title={q}>{q || "—"}</div>
        {qPdf && (
          <a
            href={`${API_BASE.replace(/\/api$/, "")}/api/media/quotation/${(q || "").replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline mt-0.5"
            data-testid="rx-quotation-pdf-link"
          >
            <Download size={11} /> PDF
          </a>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase text-slate-500">Payment link</div>
        {linkUrl ? (
          <div className="flex items-center gap-1 min-w-0">
            <a href={linkUrl} target="_blank" rel="noreferrer" className="text-primary underline truncate" data-testid="rx-payment-link-url">
              <ExternalLink size={11} className="inline mr-1" />{linkUrl}
            </a>
            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => onCopy(linkUrl)} data-testid="rx-copy-payment-link">
              <Copy size={11} />
            </Button>
          </div>
        ) : (
          <div className="text-slate-500">{linkStatus ? `status: ${linkStatus}` : "not created yet"}</div>
        )}
      </div>
      <div>
        <div className="text-[10px] uppercase text-slate-500">Order / Payment</div>
        <div className="flex flex-wrap gap-1">
          <Badge variant="outline">{order?.status || "—"}</Badge>
          <Badge className={payStatus === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{payStatus || "—"}</Badge>
        </div>
      </div>
    </div>
  );
}

export function PrescriptionReviewModal({ open, onOpenChange, order: initialOrder, onUpdated }) {
  const [rxData, setRxData] = useState(null);
  const [order, setOrder] = useState(initialOrder);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState("");
  const [defaultGst, setDefaultGst] = useState(12);
  const [expanded, setExpanded] = useState(false);

  const orderType = order?.order_type;
  const orderId = order?.id;

  const refresh = async () => {
    if (!orderId) return;
    try {
      const [rxRes, ordRes] = await Promise.all([
        api.get(`/order-mgmt/orders/${orderId}/prescription`),
        api.get(`/order-mgmt/orders/${orderId}`),
      ]);
      setRxData(rxRes.data);
      setOrder(ordRes.data);
      const existing = withStableKeys(ordRes.data?.line_items || []);
      setLines(existing.length ? computeTotals(existing).lines.map((l, i) => ({ ...l, _key: existing[i]._key })) : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load prescription");
    }
  };

  useEffect(() => {
    setOrder(initialOrder);
    if (open && initialOrder?.id) refresh();
    /* eslint-disable-next-line */
  }, [open, initialOrder?.id]);

  const liveTotals = useMemo(() => computeTotals(lines).totals, [lines]);

  const seedFromRx = async () => {
    setBusy("seed");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/lines/from-prescription`, {
        default_gst_pct: defaultGst, default_mrp: 0, replace_existing: true,
      });
      setOrder(data);
      setLines(withStableKeys(data?.line_items || []));
      toast.success("Line items seeded — please fill MRP per medicine");
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-fill failed"); }
    finally { setBusy(""); }
  };

  // v1.12: staff-managed additional charges (packaging / delivery / sample_collection / other)
  const [charges, setCharges] = useState([]);
  useEffect(() => {
    setCharges(withStableKeys(order?.additional_charges || []));
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // v1.18: unified additional-charge options across every order type
  // (packaging, delivery, home sample collection). Removed the "Other"
  // freeform option per user request.
  const addChargeOptions = [
    { v: "packaging",         l: "Packaging Charge" },
    { v: "delivery",          l: "Delivery Charge" },
    { v: "sample_collection", l: "Home Sample Collection" },
  ];
  const addCharge = () => setCharges((c) => [...c, {
    _key: _mkKey(),
    charge_type: addChargeOptions[0].v,
    label: addChargeOptions[0].l,
    amount: 0, gst_pct: 0,
  }]);
  const updCharge = (i, k, v) => setCharges((c) => c.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const rmCharge = (i) => setCharges((c) => c.filter((_, idx) => idx !== i));
  const chargesBase = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
  const chargesGst = charges.reduce((s, c) => s + Number(c.amount || 0) * Number(c.gst_pct || 0) / 100, 0);

  const saveLines = async () => {
    setBusy("save");
    const recomputed = computeTotals(lines);
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/lines`, {
        lines: recomputed.lines,
        additional_charges: charges.map((c) => ({
          charge_type: c.charge_type,
          label: c.label || null,
          amount: Number(c.amount || 0),
          gst_pct: Number(c.gst_pct || 0),
        })),
      });
      setOrder(data);
      // Preserve existing _keys across a save so inputs don't remount after saving.
      const prev = lines;
      const saved = (data && data.line_items) || [];
      setLines(saved.map((l, i) => ({ ...l, _key: prev[i]?._key || l._key || _mkKey() })));
      setCharges(withStableKeys(data?.additional_charges || []));
      toast.success("Lines & charges saved");
      onUpdated?.(data);
    } catch (e) {
      console.error("saveLines failed", e, e?.response?.data);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
        : e?.message || "Save failed";
      toast.error(`Save failed: ${msg}`);
    }
    finally { setBusy(""); }
  };

  const confirmReview = async () => {
    setBusy("confirm");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/status`, { status: "reviewed", remarks: "Staff confirmed review" });
      setOrder(data);
      toast.success("Marked as reviewed");
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirm failed"); }
    finally { setBusy(""); }
  };

  const genQuotation = async () => {
    setBusy("quotation");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/quotation`);
      setOrder(data);
      toast.success("Quotation generated");
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Quotation failed"); }
    finally { setBusy(""); }
  };

  const genPaymentLink = async () => {
    setBusy("pay");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/payment-link`, { auto_notify: true });
      setOrder(data);
      const url = data?.payment_link?.url;
      const d = data?._delivery || {};
      if (url && d.delivered) toast.success(`Payment link created & WhatsApp sent → ${url}`);
      else if (url) toast.warning(`Payment link created but WhatsApp not delivered${d.error ? `: ${d.error}` : ""}`);
      else toast.error(`Payment link not created (status: ${data?.payment_link?.status || "unknown"})`);
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment link failed"); }
    finally { setBusy(""); }
  };

  const sendWa = async (event) => {
    setBusy(`notify:${event}`);
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/notify`, { event });
      setOrder(data);
      const d = data?._delivery || {};
      if (d.delivered) toast.success(`WhatsApp ${event} sent (msg ${d.message_id || "ok"})`);
      else if (d.error) toast.error(`WhatsApp ${event} failed: ${d.error}`);
      else toast.message(`WhatsApp ${event} queued`);
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Notify failed"); }
    finally { setBusy(""); }
  };

  const runAiAnalyse = async () => {
    setBusy("analyse");
    try {
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/analyse-rx`);
      setOrder(data);
      toast.success("AI extraction complete");
      await refresh();
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI analyse failed"); }
    finally { setBusy(""); }
  };

  const genInvoice = async (notify = true) => {
    setBusy("invoice");
    try {
      const { data } = await api.post(
        `/order-mgmt/orders/${orderId}/invoice/generate?notify=${notify ? "true" : "false"}`,
      );
      setOrder(data);
      toast.success(`Invoice ${data?.invoice_number || ""} generated${notify ? " & sent on WhatsApp" : ""}`);
      onUpdated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invoice generation failed"); }
    finally { setBusy(""); }
  };

  const downloadInvoice = () => {
    if (!order?.invoice_number) { toast.error("Generate the invoice first"); return; }
    window.open(`${API_BASE}/order-mgmt/orders/${orderId}/invoice/pdf`, "_blank");
  };

  const printInvoice = () => {
    if (!order?.invoice_number) { toast.error("Generate the invoice first"); return; }
    const w = window.open(`${API_BASE}/order-mgmt/orders/${orderId}/invoice/pdf`, "_blank");
    setTimeout(() => { try { w?.print(); } catch (err) { console.warn("PDF print trigger failed; viewer will offer print:", err); } }, 800);
  };

  const resendInvoice = async () => {
    setBusy("invoice-send");
    try {
      await api.post(`/order-mgmt/orders/${orderId}/invoice/notify`);
      toast.success("Invoice re-sent on WhatsApp");
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-send failed"); }
    finally { setBusy(""); }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const payWithRazorpay = async () => {
    setBusy("razorpay");
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Failed to load Razorpay Checkout script"); return; }
      const { data } = await api.post(`/order-mgmt/orders/${orderId}/razorpay/order`);
      const { checkout, customer } = data;
      if (!checkout?.id || !checkout?.key_id) {
        toast.error("Razorpay not configured on server");
        return;
      }
      const rzp = new window.Razorpay({
        key: checkout.key_id,
        order_id: checkout.id,
        amount: checkout.amount,
        currency: checkout.currency || "INR",
        name: "Medishiv Healthcare",
        description: `Order ${order?.order_number || orderId}`,
        prefill: {
          name: customer?.name || "",
          contact: (customer?.phone || "").replace(/\D/g, ""),
          email: customer?.email || "",
        },
        theme: { color: "#0f766e" },
        handler: async (resp) => {
          try {
            const { data: verified } = await api.post(
              `/order-mgmt/orders/${orderId}/razorpay/verify`,
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
            );
            setOrder(verified);
            toast.success(`Payment verified — ${resp.razorpay_payment_id}`);
            onUpdated?.(verified);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: async () => {
            toast.message("Checkout closed — verifying payment…");
            setBusy("");
            // Auto-poll Razorpay: some customers finish payment then close
            // the modal too fast for the signed callback to fire.  This
            // reconciles the order automatically.
            try {
              const { data: pollRes } = await api.post(
                `/order-mgmt/orders/${orderId}/razorpay/poll`,
              );
              if (pollRes?.captured || pollRes?.already_paid) {
                toast.success("Payment verified ✓");
                const { data: fresh } = await api.get(`/order-mgmt/orders/${orderId}`);
                setOrder(fresh);
                onUpdated?.(fresh);
              } else {
                toast.message(pollRes?.message || "No captured payment yet — click 'Verify payment' after paying.");
              }
            } catch (err) {
              // Silent by design — user may not have paid yet. Log for debug only.
              console.debug("razorpay poll skipped:", err);
            }
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "unknown"}`);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Razorpay checkout failed");
    } finally { setBusy(""); }
  };

  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const rx = rxData?.rx_document;
  const publicMedia = rx?.media_url || rxData?.prescription_url || order?.prescription_url;
  const mimeHint = rx?.mime_type;
  const meds = (rx?.extraction?.medicines) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={expanded
          ? "!max-w-[98vw] w-[98vw] h-[95vh] max-h-[95vh] overflow-y-auto"
          : "!max-w-[1400px] w-[92vw] max-h-[92vh] overflow-y-auto"}
        data-testid="rx-review-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText size={18} /> Prescription review — order {order?.order_number || order?.id}
            <Badge variant="outline">{orderType}</Badge>
            {rxData?.rx_document_id ? (
              <Badge className="bg-emerald-100 text-emerald-800" data-testid="rx-linked-badge">AI Linked</Badge>
            ) : publicMedia ? (
              <Badge className="bg-amber-100 text-amber-800">Legacy attachment</Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800">No prescription</Badge>
            )}
            {!rxData?.rx_document_id && publicMedia && (
              <Button size="sm" onClick={runAiAnalyse} disabled={busy === "analyse"} data-testid="rx-run-ai-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy === "analyse" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Run AI extraction now
              </Button>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={refresh} data-testid="rx-refresh-btn"><RefreshCw size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setExpanded((v) => !v)} data-testid="rx-fullscreen-btn" title={expanded ? "Compact" : "Expand"}>
                {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <StatusBanner order={order} onCopy={copy} />

        <div className="grid grid-cols-2 gap-4">
          {/* Left column: media + AI extracted */}
          <div className="space-y-3">
            <MediaViewer url={publicMedia} mime={mimeHint} />
            {rx && (
              <div className="text-[11px] text-slate-500 grid grid-cols-2 gap-1">
                <div>rx doc: <span className="font-mono">{rx.id}</span></div>
                <div>meds extracted: {meds.length}</div>
                <div>classification: {rx.classification?.image_class || "—"}</div>
                <div>confidence: {((rx.confidence_score || 0) * 100).toFixed(0)}%</div>
              </div>
            )}
            <div>
              <div className="text-xs font-semibold uppercase text-slate-500 mb-1">AI extracted ({meds.length})</div>
              <ExtractedList meds={meds} />
            </div>
          </div>

          {/* Right column: line items + actions */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={seedFromRx} disabled={busy === "seed" || !rxData?.rx_document_id} data-testid="rx-seed-btn">
                {busy === "seed" ? <Loader2 className="animate-spin" size={14} /> : <Wand2 size={14} />} Auto-fill from prescription
              </Button>
              <span className="text-[11px] text-slate-500">MRP, Qty, Disc & GST are editable per medicine.</span>
            </div>
            <div className="max-h-[260px] overflow-auto pr-1">
              {/* v1.52 — pass raw `lines` (not recomputed) so React sees the
                  same object references each render and the input focus stays
                  put. `line_total` for display is derived from `liveTotals`. */}
              <LineGrid lines={lines.map((l, i) => ({
                ...l,
                line_total: computeTotals(lines).lines[i]?.line_total,
              }))} setLines={setLines} orderType={orderType} />
            </div>

            {/* v1.12: Additional Charges — optional, always separate from products */}
            <div className="border rounded-lg p-3 bg-slate-50/60 space-y-2" data-testid="rx-additional-charges">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase text-slate-600">Additional Charges (optional)</div>
                <Button size="sm" variant="outline" onClick={addCharge} data-testid="rx-add-charge-btn">
                  <Plus size={12} /> Add Charge
                </Button>
              </div>
              {charges.length === 0 && (
                <div className="text-[11px] text-slate-500">No additional charges. Charges are optional and never auto-applied.</div>
              )}
              {charges.map((c, i) => (
                <div key={c._key || c.id || `charge-${i}`} className="flex gap-1.5 items-center flex-wrap" data-testid={`rx-charge-row-${i}`}>
                  <select value={c.charge_type}
                    onChange={(e) => {
                      const opt = addChargeOptions.find((o) => o.v === e.target.value);
                      updCharge(i, "charge_type", e.target.value);
                      updCharge(i, "label", opt?.l || "");
                    }}
                    className="h-8 text-xs border rounded px-2 bg-white" data-testid={`rx-charge-type-${i}`}>
                    {addChargeOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  <Input className="no-spinner w-24 h-8 text-xs text-right" type="number" inputMode="decimal"
                    placeholder="Amount" value={c.amount ?? ""}
                    onChange={(e) => updCharge(i, "amount", e.target.value)} data-testid={`rx-charge-amount-${i}`} />
                  <Input className="no-spinner w-20 h-8 text-xs text-right" type="number" inputMode="decimal"
                    placeholder="GST %" value={c.gst_pct ?? ""}
                    onChange={(e) => updCharge(i, "gst_pct", e.target.value)} data-testid={`rx-charge-gst-${i}`} />
                  <div className="w-24 text-right font-medium tabular-nums text-emerald-800 text-xs">
                    {inr(Number(c.amount || 0) * (1 + Number(c.gst_pct || 0) / 100))}
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => rmCharge(i)} data-testid={`rx-charge-remove-${i}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))}
              {charges.length > 0 && (
                <div className="text-[11px] text-slate-600 flex gap-4 pt-1 border-t">
                  <span>Charges base: <b>{inr(chargesBase)}</b></span>
                  <span>Charges GST: <b>{inr(chargesGst)}</b></span>
                  <span>Charges total: <b>{inr(chargesBase + chargesGst)}</b></span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs border-t pt-2" data-testid="rx-totals">
              <div><div className="text-[10px] text-slate-500">Products Taxable</div>{inr(liveTotals.taxable_amount)}</div>
              <div><div className="text-[10px] text-slate-500">Additional Charges</div>{inr(chargesBase)}</div>
              <div><div className="text-[10px] text-slate-500">Total Taxable</div>{inr((liveTotals.taxable_amount || 0) + chargesBase)}</div>
              <div><div className="text-[10px] text-slate-500">Product GST</div>{inr(liveTotals.gst_total || 0)}</div>
              <div><div className="text-[10px] text-slate-500">Charges GST</div>{inr(chargesGst)}</div>
              <div className="text-emerald-800"><div className="text-[10px]">Gross Invoice</div><div className="font-bold" data-testid="rx-grand-total">{inr((liveTotals.taxable_amount || 0) + chargesBase + (liveTotals.gst_total || 0) + chargesGst)}</div></div>
            </div>

            {/* TDS */}
            <div className="flex items-center gap-2 border rounded-lg p-2 bg-slate-50">
              <div className="text-[11px] uppercase text-slate-500 font-semibold">TDS</div>
              <select
                className="h-8 text-xs border rounded px-2 bg-white"
                value={order?.tds?.section || ""}
                onChange={(e) => {
                  const sel = TDS_SECTIONS_UI.find((t) => t.code === e.target.value);
                  api.post(`/order-mgmt/orders/${orderId}/tds`, {
                    section: sel?.code || null, rate: sel?.rate || 0,
                  }).then(({ data }) => {
                    // v1.19: preserve unsaved local lines & charges edits —
                    // only refresh TDS section/rate/totals from the server.
                    setOrder((prev) => ({ ...prev, ...data,
                                          line_items: prev?.line_items ?? data?.line_items,
                                          additional_charges: prev?.additional_charges ?? data?.additional_charges }));
                    toast.success(`TDS ${sel?.code || "cleared"}`);
                  }).catch((err) => toast.error(err?.response?.data?.detail || "TDS update failed"));
                }}
                data-testid="rx-tds-section"
              >
                {TDS_SECTIONS_UI.map((t) => (
                  <option key={t.code || "none"} value={t.code}>{t.label}</option>
                ))}
              </select>
              <Input
                className="no-spinner w-20 h-8 text-xs text-right"
                type="number"
                inputMode="decimal"
                placeholder="Rate %"
                value={order?.tds?.rate ? String(order.tds.rate) : ""}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value) || 0;
                  api.post(`/order-mgmt/orders/${orderId}/tds`, {
                    section: order?.tds?.section || null, rate,
                  }).then(({ data }) => {
                    // v1.19: preserve unsaved local edits — only refresh
                    // TDS/totals, keep the user's in-progress lines & charges.
                    setOrder((prev) => ({ ...prev, ...data,
                                          line_items: prev?.line_items ?? data?.line_items,
                                          additional_charges: prev?.additional_charges ?? data?.additional_charges }));
                  }).catch(() => {});
                }}
                data-testid="rx-tds-rate"
              />
              <div className="text-xs text-slate-600 ml-auto">
                TDS amount:&nbsp;<span className="font-semibold" data-testid="rx-tds-amount">{inr((order?.totals?.tds_amount) || 0)}</span>
              </div>
            </div>

            <Card><CardContent className="p-3 space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase text-slate-500">Commercial actions</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={saveLines} disabled={busy === "save"} data-testid="rx-save-lines-btn">
                  {busy === "save" ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save lines
                </Button>
                <Button size="sm" onClick={confirmReview} disabled={busy === "confirm"} data-testid="rx-confirm-review-btn">
                  {busy === "confirm" ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Confirm review
                </Button>
                <Button size="sm" onClick={genQuotation} disabled={busy === "quotation"} data-testid="rx-gen-quotation-btn">
                  {busy === "quotation" ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />} Generate quotation
                </Button>
                <Button size="sm" onClick={genPaymentLink} disabled={busy === "pay"} data-testid="rx-gen-payment-link-btn">
                  {busy === "pay" ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />} Create payment link + WA
                </Button>
                <Button size="sm" onClick={payWithRazorpay} disabled={busy === "razorpay"} data-testid="rx-razorpay-checkout-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {busy === "razorpay" ? <Loader2 className="animate-spin" size={14} /> : <CreditCard size={14} />} Pay now (Razorpay Checkout)
                </Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  setBusy("verify");
                  try {
                    const { data } = await api.post(`/order-mgmt/orders/${orderId}/razorpay/poll`);
                    if (data?.captured || data?.already_paid) {
                      toast.success("Payment verified ✓");
                      const { data: fresh } = await api.get(`/order-mgmt/orders/${orderId}`);
                      setOrder(fresh); onUpdated?.(fresh);
                    } else { toast.message(data?.message || "No captured payment yet"); }
                  } catch (e) { toast.error(e?.response?.data?.detail || "Poll failed"); }
                  finally { setBusy(""); }
                }} disabled={busy === "verify"} data-testid="rx-razorpay-verify-btn">
                  {busy === "verify" ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} Verify payment
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={() => sendWa("quotation")} data-testid="rx-wa-quotation-btn"><MessageCircle size={14} /> Send quotation WA</Button>
                <Button size="sm" variant="outline" onClick={() => sendWa("payment_link")} data-testid="rx-wa-payment-link-btn"><MessageCircle size={14} /> Re-send payment link WA</Button>
                <Button size="sm" variant="outline" onClick={() => sendWa("payment_confirmation")} data-testid="rx-wa-payment-confirm-btn"><MessageCircle size={14} /> Send payment confirmation</Button>
              </div>
              {/* Tax Invoice */}
              <div className="border-t pt-2 mt-1">
                <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Tax Invoice</div>
                <div className="flex flex-wrap gap-2 items-center">
                  {order?.invoice_number ? (
                    <>
                      <Badge className="bg-emerald-100 text-emerald-800" data-testid="rx-invoice-badge">{order.invoice_number}</Badge>
                      <Badge variant="outline" className="text-[10px]">{order.invoice_status || "generated"}</Badge>
                      <Button size="sm" variant="outline" onClick={downloadInvoice} data-testid="rx-invoice-download-btn"><Download size={14} /> Download</Button>
                      <Button size="sm" variant="outline" onClick={printInvoice} data-testid="rx-invoice-print-btn"><FileText size={14} /> Print</Button>
                      <Button size="sm" variant="outline" onClick={resendInvoice} disabled={busy === "invoice-send"} data-testid="rx-invoice-resend-btn"><MessageCircle size={14} /> Re-send on WhatsApp</Button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs text-slate-500">No invoice yet (auto-generated on payment)</span>
                      <Button size="sm" onClick={() => genInvoice(true)} disabled={busy === "invoice"} data-testid="rx-invoice-gen-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {busy === "invoice" ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />} Generate & send
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent></Card>
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="rx-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PrescriptionReviewModal;
